import React, { useMemo } from "react";
import { FaArrowDown } from "react-icons/fa6";
import "./ServicesHero.css";
import heroImage from "../../../../assets/images/services/serviceshero/serviceshero.jpg";
import { resolveMediaUrl } from "../../../../utils/media";

const fallbackServicesHero = {
  tag: "SERVICES",
  titleLine1: "Empowering Your Projects",
  titleLine2: "with Expert Guidance",
  ctaText: "Explore all services",
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const hasSectionData = (section) => {
  if (!section || typeof section !== "object") return false;
  return Object.values(section).some((value) => {
    if (value === null || typeof value === "undefined") return false;
    if (typeof value === "string") return value.trim().length > 0;
    return true;
  });
};

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

const coalesceHeroPayload = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") return tryParseJsonObject(raw) || null;
  if (typeof raw !== "object") return null;
  let base = { ...raw };
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...raw, ...raw.data };
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return base;
};

const ServicesHero = ({ content }) => {
  const view = useMemo(() => {
    const merged = coalesceHeroPayload(content);
    const section = hasSectionData(merged) ? merged : fallbackServicesHero;
    const singleTitle = pickString(section.title);
    let titleLine1 = pickString(section.titleLine1, section.heading);
    let titleLine2 = pickString(section.titleLine2, section.subheading);
    if (!titleLine1 && singleTitle) {
      if (singleTitle.includes("\n")) {
        const parts = singleTitle.split("\n").map((p) => p.trim());
        titleLine1 = parts[0] || "";
        titleLine2 = parts.slice(1).join(" ").trim() || titleLine2;
      } else {
        titleLine1 = singleTitle;
      }
    }

    return {
      tag: pickString(section.tag, section.sectionTag) || fallbackServicesHero.tag,
      titleLine1: titleLine1 || fallbackServicesHero.titleLine1,
      titleLine2:
        titleLine2 || (singleTitle && !singleTitle.includes("\n") ? "" : fallbackServicesHero.titleLine2),
      ctaText: pickString(section.ctaText, section.linkText, section.exploreText) || fallbackServicesHero.ctaText,
      imageSrc:
        resolveMediaUrl(section.image || section.bgImage || section.backgroundImage || section.heroImage || section.mediaUrl || section.photo) ||
        heroImage,
    };
  }, [content]);

  const scrollToContent = () => {
    document
      .querySelector(".services-page-content-guide")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="servicesHero" aria-label="Services hero">
      <div className="servicesHero-bg">
        <img
          src={view.imageSrc}
          alt=""
          className="servicesHero-bgImg"
          draggable={false}
          onError={(e) => {
            if (e.currentTarget.src !== heroImage) {
              e.currentTarget.src = heroImage;
            }
          }}
        />
      </div>

      <div className="servicesHero-overlay" aria-hidden="true" />

      <div className="servicesHero-inner">
        <span className="servicesHero-tag">{view.tag}</span>

        <h1 className="servicesHero-title">
          {view.titleLine1}
          {view.titleLine2 ? (
            <>
              <br />
              <span>{view.titleLine2}</span>
            </>
          ) : null}
        </h1>

        <button
          type="button"
          className="servicesHero-cta"
          onClick={scrollToContent}
        >
          {view.ctaText}
          <FaArrowDown className="servicesHero-ctaIcon" aria-hidden />
        </button>
      </div>
    </section>
  );
};

export default ServicesHero;